// Users.jsx — the portal's own accounts.
//
// ── WHAT THIS SCREEN HAS TO GET RIGHT ──
// Who may create whom. A superadmin makes admins and operators; an admin makes
// operators only; an operator makes nobody. The server enforces all of it, so
// the job here is to never offer a choice the server will refuse — a form that
// submits into a 403 teaches an admin that the portal is unreliable, when in
// fact it was the portal that lied.
//
// Two consequences worth knowing before editing:
//
//  - The store picker is superadmin-only. ResolveAccountScope ignores the
//    body's storeId for a role-2 caller and takes the store off its token, so
//    showing the picker to an admin offers a choice that cannot be honoured.
//
//  - Delete is superadmin-only. DELETE /admin/accounts/{id} refuses every
//    other caller: an admin may hire an operator into its own store but not
//    erase one's login history.

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  resetStatus,
} from "../../../slice/adminUser/adminUser";
import api from "../../../api/axios";
import {
  PageShell,
  DataTable,
  Button,
  Pill,
  Avatar,
  RowActions,
  Modal,
  Field,
  PlusIcon,
} from "../component/AdminUi";

/* Numeric in the database, words on the screen. */
const ROLES = [
  { value: 1, label: "Super admin", tone: "forest" },
  { value: 2, label: "Admin", tone: "gold" },
  { value: 3, label: "Operator", tone: "leaf" },
];

const roleOf = (n) => ROLES.find((r) => r.value === Number(n));

/* The IST stamp the rest of the portal uses. */
const whenIst = (iso) =>
  new Date(iso).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function Users() {
  const dispatch = useDispatch();
  const { list: users, loading, success, error } = useSelector(
    (s) => s.adminUser
  );

  const myRole = Number(localStorage.getItem("role"));
  const isSuperadmin = myRole === 1;

  /* Roles this account may hand out. Everything on the screen keys off this:
     no Add button when it is empty, no edit pencil on a row above it. */
  const creatable = ROLES.filter((r) => {
    if (myRole === 1) return r.value === 2 || r.value === 3;
    if (myRole === 2) return r.value === 3;
    return false;
  });

  const [query, setQuery] = useState("");
  const [stores, setStores] = useState([]);

  /* The form. One object rather than six useStates, so opening the modal is a
     single assignment and closing it cannot leave half the fields behind. */
  const blank = {
    id: null,
    username: "",
    role: creatable[0]?.value ?? 3,
    password: "",
    phone: "",
    storeId: "",
  };
  const [form, setForm] = useState(blank);
  const [open, setOpen] = useState(false);
  const [peek, setPeek] = useState(false);
  const [problem, setProblem] = useState(null);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  useEffect(() => {
    dispatch(fetchAdminUsers());
  }, [dispatch]);

  useEffect(() => {
    /* A bare array, and the store's id is `id` — still the 24-hex id an
       account's storeId carries, so the two keep comparing like for like. */
    api
      .get("/admin/stores")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setStores(list);
        const fallback = list.find((s) => s.isDefault) || list[0];
        if (fallback) setForm((f) => ({ ...f, storeId: f.storeId || fallback.id }));
      })
      .catch(() => setStores([]));
  }, []);

  useEffect(() => {
    if (!success) return;
    dispatch(fetchAdminUsers());
    dispatch(resetStatus());
    close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  const shown = users.filter((u) =>
    u.username.toLowerCase().includes(query.trim().toLowerCase())
  );

  const storeNameFor = (user) =>
    Number(user.role) === 1
      ? "All stores"
      : stores.find((s) => String(s.id) === String(user.storeId))?.name ||
        stores.find((s) => s.isDefault)?.name ||
        "—";

  /* ── opening and closing ───────────────────────────────────────────────── */

  const openNew = () => {
    setForm({
      ...blank,
      storeId: stores.find((s) => s.isDefault)?.id || stores[0]?.id || "",
    });
    setProblem(null);
    setPeek(false);
    setOpen(true);
  };

  const openEdit = (user) => {
    setForm({
      id: user.id,
      username: user.username,
      role: Number(user.role),
      password: "",
      phone: user.phone || "",
      storeId: user.storeId || stores.find((s) => s.isDefault)?.id || "",
    });
    setProblem(null);
    setPeek(false);
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setForm(blank);
    setProblem(null);
    setPeek(false);
    dispatch(resetStatus());
  };

  /* ── saving ────────────────────────────────────────────────────────────── */

  const save = () => {
    const username = form.username.trim();
    const digits = form.phone.replace(/\D/g, "").slice(-10);
    const editing = form.id != null;

    /* Said on the form, not in an alert box. An alert is modal on top of a
       modal and loses whatever the person was about to fix. */
    if (!username) return setProblem("Give the account a username.");
    if (!editing && !form.password)
      return setProblem("Set a password for the new account.");
    if (!editing && digits.length !== 10)
      return setProblem("Enter a 10-digit mobile number.");
    if (digits && !/^[6-9]/.test(digits))
      return setProblem("That does not look like an Indian mobile number.");
    if (!creatable.some((r) => r.value === Number(form.role)))
      return setProblem("You cannot assign that role.");
    /* role 2/3 need a store; superadmin is store-less and the server forces
       its storeId to null regardless. */
    if (
      isSuperadmin &&
      Number(form.role) !== 1 &&
      !form.storeId &&
      stores.length > 0
    )
      return setProblem("Pick the store this account belongs to.");

    const payload = {
      username,
      role: Number(form.role),
      ...(isSuperadmin && Number(form.role) !== 1 && form.storeId
        ? { storeId: form.storeId }
        : {}),
      /* On edit an empty password box means "leave it alone". */
      ...(form.password ? { password: form.password } : {}),
      ...(digits ? { phone: digits } : {}),
    };

    setProblem(null);
    if (editing) dispatch(updateAdminUser({ id: form.id, data: payload }));
    else dispatch(createAdminUser({ ...payload, password: form.password }));
  };

  const remove = (user) => {
    if (
      window.confirm(
        `Delete ${user.username}? Their login history goes with them.`
      )
    ) {
      dispatch(deleteAdminUser(user.id));
    }
  };

  /* ── render ────────────────────────────────────────────────────────────── */

  return (
    <PageShell
      title="Users"
      count={
        loading && !users.length
          ? "Loading…"
          : `${users.length} account${users.length === 1 ? "" : "s"}` +
            (query ? ` · ${shown.length} matching` : "")
      }
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search by username",
      }}
      actions={
        creatable.length > 0 ? (
          <Button kind="primary" onClick={openNew}>
            <PlusIcon />
            Add user
          </Button>
        ) : null
      }
    >
      <DataTable
        columns={["User", "Role", "Mobile", "Store", "Last login", ""]}
        loading={loading}
        error={error}
        isEmpty={!loading && shown.length === 0}
        emptyTitle={query ? "No account matches that" : "No accounts yet"}
        emptyHint={
          query
            ? "Try a shorter search."
            : creatable.length > 0
            ? "Add the first one with the button above."
            : "Your role cannot create accounts."
        }
      >
        {shown.map((user) => {
          const role = roleOf(user.role);
          const manageable = creatable.some(
            (r) => r.value === Number(user.role)
          );
          return (
            <tr key={user.id}>
              <td>
                <span className="ui-who">
                  <Avatar
                    label={user.username}
                    accent={Number(user.role) === 1}
                  />
                  <span className="ui-who__name">{user.username}</span>
                </span>
              </td>
              <td>
                <Pill tone={role?.tone || "grey"}>
                  {role?.label || "Unknown"}
                </Pill>
              </td>
              <td className="ui-num">
                {user.phone ? (
                  /* \d, not a literal "d" — the class was once written
                     without its backslashes, so the group never matched and
                     every number rendered unspaced. */
                  user.phone.replace(/^(\d{5})(\d{5})$/, "$1 $2")
                ) : (
                  <span className="ui-muted">not set</span>
                )}
              </td>
              <td>{storeNameFor(user)}</td>
              <td className="ui-num">
                {/* Null until the account signs in once, so "Never" is a real
                    answer here and not a missing field. */}
                {user.lastLoginAt ? (
                  whenIst(user.lastLoginAt)
                ) : (
                  <span className="ui-muted">Never</span>
                )}
              </td>
              <td>
                <RowActions
                  onEdit={manageable ? () => openEdit(user) : undefined}
                  onDelete={
                    isSuperadmin && manageable ? () => remove(user) : undefined
                  }
                />
              </td>
            </tr>
          );
        })}
      </DataTable>

      <Modal
        open={open}
        title={form.id ? `Edit ${form.username}` : "Add user"}
        onClose={close}
        footer={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button kind="primary" onClick={save} disabled={loading}>
              {form.id ? "Save changes" : "Create account"}
            </Button>
          </>
        }
      >
        {problem ? <div className="ui-alert ui-alert--error">{problem}</div> : null}

        <Field label="Username" required>
          <input
            className="ui-input"
            value={form.username}
            onChange={set("username")}
            autoComplete="off"
            placeholder="e.g. counter2"
          />
        </Field>

        <Field label="Role" required>
          <select
            className="ui-select"
            value={form.role}
            onChange={set("role")}
          >
            {creatable.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </Field>

        {isSuperadmin && Number(form.role) !== 1 && stores.length > 0 ? (
          <Field
            label="Store"
            required
            hint="Admins and operators work in exactly one store."
          >
            <select
              className="ui-select"
              value={form.storeId}
              onChange={set("storeId")}
            >
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.isDefault ? " (default)" : ""}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        <Field
          label="Mobile"
          required={!form.id}
          hint="Used for the staff sign-in code."
        >
          <input
            className="ui-input"
            value={form.phone}
            onChange={set("phone")}
            inputMode="numeric"
            maxLength={10}
            placeholder="10 digits"
          />
        </Field>

        <Field
          label="Password"
          required={!form.id}
          hint={form.id ? "Leave blank to keep the current password." : undefined}
        >
          <span className="ui-field__control">
            <input
              className="ui-input"
              type={peek ? "text" : "password"}
              value={form.password}
              onChange={set("password")}
              autoComplete="new-password"
              placeholder={form.id ? "Unchanged" : "Set a password"}
              style={{ paddingRight: 68 }}
            />
            <button
              type="button"
              className="ui-field__peek"
              onClick={() => setPeek((v) => !v)}
              tabIndex={-1}
            >
              {peek ? "Hide" : "Show"}
            </button>
          </span>
        </Field>
      </Modal>
    </PageShell>
  );
}
